import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  KeyRound, 
  LogOut, 
  Moon, 
  Bell, 
  Shield, 
  Lock, 
  Smartphone,
  Globe,
  CheckCircle,
  AlertCircle,
  User,
  UserCog,
  Power
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Card, Button } from "../components/ui";
import { Modal } from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import ChangePasswordModal from "./auth/ChangePasswordModal";

export default function Settings() {
  const { logout } = useAuth();
  const [notifications, setNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
    } finally {
      setIsLoggingOut(false);
      setLogoutModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-6 py-6 lg:px-8 max-w-4xl mx-auto space-y-6">
        <PageHeader 
          title="Settings" 
          subtitle="Manage security, preferences, and account settings." 
        />

        {/* Security Section */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 hover:shadow-emerald-200/50 transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg shadow-emerald-500/20">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Security</h3>
              <p className="text-xs text-gray-500">Manage your account security settings</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Change Password with Modal */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-emerald-50/50 rounded-xl border border-emerald-100/50 hover:border-emerald-200 transition-colors duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <KeyRound className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Change Password</p>
                  <p className="text-xs text-gray-500">Reset using OTP sent to your registered phone</p>
                </div>
              </div>
              <button
                onClick={() => setPasswordModalOpen(true)}
                className="mt-3 sm:mt-0 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-[1.02] transition-all duration-200 w-full sm:w-auto"
              >
                <KeyRound size={14} />
                Change Password
              </button>
            </div>
          </div>
        </Card>

        {/* Preferences Section */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 hover:shadow-emerald-200/50 transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg shadow-emerald-500/20">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Preferences</h3>
              <p className="text-xs text-gray-500">Customize your experience</p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Dark Mode */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-200 transition-colors duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Moon className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Dark Mode</p>
                  <p className="text-xs text-gray-500">Reduce glare in low-light environments</p>
                  <span className="inline-block mt-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                    Coming Soon
                  </span>
                </div>
              </div>
              <div className="mt-3 sm:mt-0 opacity-50 cursor-not-allowed">
                <ToggleSwitch
                  checked={darkMode}
                  onChange={setDarkMode}
                  label="Dark Mode"
                  disabled
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Session & Logout Section */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 hover:shadow-emerald-200/50 transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg shadow-red-500/20">
              <LogOut className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Session</h3>
              <p className="text-xs text-gray-500">Manage your current session</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-red-50/50 rounded-xl border border-red-200/50">
            <div>
              <p className="text-sm font-semibold text-gray-800">Sign Out</p>
              <p className="text-xs text-gray-500">End your session on this device</p>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setLogoutModalOpen(true)}
              className="mt-3 sm:mt-0 bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg hover:shadow-red-500/25 transition-all duration-200"
            >
              <LogOut size={16} className="mr-1.5" />
              Sign Out
            </Button>
          </div>

          {/* Session Info */}
          <div className="mt-4 flex items-center gap-2 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100/50">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              <span>Secure session active</span>
            </div>
            <span className="w-px h-4 bg-emerald-200" />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Smartphone className="w-3.5 h-3.5 text-emerald-500" />
              <span>This device</span>
            </div>
            <span className="w-px h-4 bg-emerald-200" />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Lock className="w-3.5 h-3.5 text-emerald-500" />
              <span>SSL Encrypted</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSuccess={() => {
          // Optional: Show success toast or refresh user data
        }}
      />

      {/* Logout Confirmation Modal */}
      <Modal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title="Sign Out"
        description="Are you sure you want to sign out of your account?"
        maxWidth="max-w-md"
        className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-red-50/50 rounded-xl border border-red-200/50">
            <div className="p-3 bg-red-100 rounded-full">
              <Power className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">You'll be signed out</p>
              <p className="text-xs text-gray-500">You can sign back in anytime using your credentials.</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-lg border border-amber-200/50">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-amber-700">
              Any unsaved changes will be lost. Make sure to save your work before signing out.
            </p>
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <Button 
              variant="ghost" 
              onClick={() => setLogoutModalOpen(false)}
              className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleLogout} 
              loading={isLoggingOut}
              className="bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg hover:shadow-red-500/25 transition-all duration-200"
            >
              {isLoggingOut ? 'Signing out...' : 'Yes, Sign Out'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Toggle Switch Component
function ToggleSwitch({ checked, onChange, label, disabled = false }) {
  return (
    <button
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative group flex items-center gap-3 transition-all duration-300 ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
    >
      {/* Toggle Track */}
      <div 
        className={`relative h-7 w-12 rounded-full transition-all duration-300 ${
          checked 
            ? 'bg-gradient-to-r from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/30' 
            : 'bg-gray-300 group-hover:bg-gray-400'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        {/* Toggle Thumb */}
        <div 
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-md transition-all duration-300 transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          } ${checked ? 'shadow-emerald-500/30' : 'shadow-gray-400/30'}
          group-hover:scale-105 group-active:scale-95`}
        >
          {/* Inner Icon */}
          <div className="flex items-center justify-center h-full">
            {checked ? (
              <CheckCircle className="w-3 h-3 text-emerald-500" />
            ) : (
              <div className="w-3 h-3 rounded-full border-2 border-gray-300" />
            )}
          </div>
        </div>

        {/* Ripple Effect */}
        <div className={`absolute inset-0 rounded-full transition-all duration-500 ${
          checked ? 'bg-emerald-400/20 scale-100' : 'bg-transparent scale-0'
        } group-hover:scale-100`} />
      </div>
    </button>
  );
}